import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import LoadingSpinner from '../shared/LoadingSpinner';

// The no-org landing: a signed-in user with no membership. If they have a
// saved registration draft (see /api/signup/resume) the screen says so and
// sends them back to where they left off, not to step 1.
function NoOrgWelcome({ profile, signOut }) {
  const [draft, setDraft] = useState(undefined); // undefined = loading, null = none
  useEffect(() => {
    api.getSignupResume().then((r) => setDraft(r.draft || null)).catch(() => setDraft(null));
  }, []);
  const orgName = draft?.org_form?.name;
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="text-center max-w-sm">
        <div className="w-[48px] h-[48px] gradient-logo rounded-[14px] flex items-center justify-center text-[18px] font-extrabold text-white shadow-glow mx-auto mb-5">
          ML
        </div>
        {draft ? (
          <>
            <h2 className="text-[20px] font-bold text-primary-c mb-2">Welcome back{profile?.first_name ? `, ${profile.first_name}` : ''}</h2>
            <p className="text-[13px] text-muted-c mb-6">
              Your registration{orgName ? ` for ${orgName}` : ''} is saved{draft.step >= 4 ? ' — the only step left is payment' : ''}. Pick up right where you left off.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-[20px] font-bold text-primary-c mb-2">Welcome to MediLink</h2>
            <p className="text-[13px] text-muted-c mb-6">You're not a member of any organization yet. Register your practice or wait for an invitation.</p>
          </>
        )}
        <div className="flex flex-col gap-3">
          <Link
            to="/register"
            className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all text-center"
          >
            {draft === undefined ? 'Loading…' : draft ? (draft.step >= 4 ? 'Continue to payment' : 'Continue setup') : 'Register Your Practice'}
          </Link>
          {profile?.is_platform_admin && (
            <Link
              to="/admin"
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all text-center"
            >
              Go to Admin Panel
            </Link>
          )}
          <button onClick={signOut} className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors mt-2">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ allowedRoles }) {
  const { session, loading: authLoading, profile, needsMfa, signOut } = useAuth();
  const { currentOrg, currentRole, loading: orgLoading } = useOrg();
  const location = useLocation();

  if (authLoading || orgLoading || (session && !profile)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if MFA is required but not verified
  if (profile && needsMfa()) {
    return <Navigate to="/mfa/verify" replace />;
  }

  // Platform admins without an org go straight to admin
  if (!currentOrg && profile?.is_platform_admin) {
    return <Navigate to="/admin" replace />;
  }

  // Check if user has an org membership
  if (!currentOrg) {
    return <NoOrgWelcome profile={profile} signOut={signOut} />;
  }

  // Role-based routing — block rendering until role is verified
  if (currentRole) {
    // Client users: must go to /client routes
    if (currentRole === 'client') {
      if (allowedRoles && allowedRoles.includes('client')) {
        return <Outlet />;
      }
      return <Navigate to="/client" replace />;
    }

    // Staff users: must go to /app routes
    if (allowedRoles && !allowedRoles.includes(currentRole)) {
      return <Navigate to="/app" replace />;
    }
  }

  return <Outlet />;
}
